import { useCallback, useState } from "react";
import { createRoute, useNavigate, useParams } from "@tanstack/react-router";
import type { z } from "zod";
import { BacklogStatus, BacklogView, backlogRouteSearchSchema, type BacklogSearch } from "@amber/collection/ui";
import { AppFrame } from "../shell/AppFrame";
import { usePageTitle } from "../shell/title";
import { rootRoute } from "./root";

function BacklogRoute({ search, itemId }: { search: z.infer<typeof backlogRouteSearchSchema>; itemId: string | null }) {
  usePageTitle("Backlog");
  const navigate = useNavigate();
  const { add, ...filters } = search;
  const [adding, setAdding] = useState<string | null>(add ?? null);
  const go = useCallback(
    (id: string | null, next: BacklogSearch) => {
      void navigate(
        id ? { to: "/backlog/$itemId", params: { itemId: id }, search: next } : { to: "/backlog", search: next },
      );
    },
    [navigate],
  );
  const onSearch = useCallback(
    (next: BacklogSearch) => {
      go(itemId, next);
    },
    [go, itemId],
  );
  const onOpenItem = useCallback(
    (id: string | null) => {
      go(id, filters);
    },
    [go, filters],
  );
  return (
    <AppFrame view="Backlog" status={<BacklogStatus />}>
      {() => (
        <BacklogView
          search={filters}
          itemId={itemId}
          adding={adding}
          onSearch={onSearch}
          onOpenItem={onOpenItem}
          onAdd={setAdding}
        />
      )}
    </AppFrame>
  );
}

// DI-114: the drawer route is a child so the list stays mounted and keeps its selection, hidden columns and
// collapsed groups while the drawer opens and closes.
export const backlogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/backlog",
  validateSearch: backlogRouteSearchSchema,
  component: function BacklogListRoute() {
    const { itemId } = useParams({ strict: false });
    return <BacklogRoute search={backlogRoute.useSearch()} itemId={itemId ?? null} />;
  },
});

export const backlogItemRoute = createRoute({
  getParentRoute: () => backlogRoute,
  path: "$itemId",
  component: () => null,
});
